import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { subscriptionService } from "../../services/subscription";
import { getApiErrorMessage } from "../../utils/apiErrors";

const CANCELLATION_REASONS = [
  {
    label: "Too expensive",
    clarification: "What would make the price feel fair for the value you got?",
  },
  {
    label: "Not enough data for my niche",
    clarification: "What niche or data were you hoping to find?",
  },
  {
    label: "Missing features I need",
    clarification: "What are we missing?",
  },
  {
    label: "Didn't use it enough",
    clarification: "What got in the way of using it more?",
  },
  {
    label: "Switching to a competitor",
    clarification: "What are you switching to, and why?",
  },
  {
    label: "Just testing / evaluating",
    clarification: "What did you think? What was most useful?",
  },
  {
    label: "Other (please specify)",
    clarification: "Tell us more",
  },
];

const SubscriptionCancel = () => {
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [otherDetail, setOtherDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const selectedReason = CANCELLATION_REASONS.find((option) => option.label === reason);
  const isOther = reason === "Other (please specify)";

  const close = () => {
    navigate("/account/subscription/");
  };

  const onReasonChange = (value) => {
    setReason(value);
    setDetail("");
    setOtherDetail("");
  };

  const submitCancellation = (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    subscriptionService.cancelSubscription(
      {
        reason,
        detail,
        other_detail: otherDetail,
      },
      () => {
        setSubmitting(false);
        navigate("/account/subscription/?success=cancel");
      },
      (requestError) => {
        setSubmitting(false);
        setError(getApiErrorMessage(requestError, "Could not cancel your plan. Please try again."));
      }
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-theme-secondary flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold text-theme-text">Cancel your plan</h2>
          <p className="mt-0.5 text-sm text-theme-text opacity-70">
            Tell us what happened before we complete the cancellation.
          </p>
        </div>
        <button
          type="button"
          onClick={close}
          className="p-2 rounded-lg text-theme-text opacity-70 hover:opacity-100 hover:bg-theme-secondary transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={submitCancellation} className="space-y-5">
            {error && (
              <div className="rounded-lg border border-theme-secondary bg-theme-secondary bg-opacity-40 px-4 py-3 text-sm text-theme-text">
                {error}
              </div>
            )}

            <label className="block">
              <span className="text-sm font-medium text-theme-text">Reason for cancelling</span>
              <select
                value={reason}
                onChange={(event) => onReasonChange(event.target.value)}
                required
                className="mt-2 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
              >
                <option value="">Select a reason</option>
                {CANCELLATION_REASONS.map((option) => (
                  <option key={option.label} value={option.label}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {selectedReason && !isOther && (
              <label className="block">
                <span className="text-sm font-medium text-theme-text">{selectedReason.clarification}</span>
                <textarea
                  value={detail}
                  onChange={(event) => setDetail(event.target.value)}
                  required
                  rows={4}
                  className="mt-2 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
                />
              </label>
            )}

            {isOther && (
              <label className="block">
                <span className="text-sm font-medium text-theme-text">{selectedReason.clarification}</span>
                <textarea
                  value={otherDetail}
                  onChange={(event) => setOtherDetail(event.target.value)}
                  required
                  rows={4}
                  className="mt-2 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
                />
              </label>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2.5 bg-theme-primary text-theme-button rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                style={{ backgroundColor: "var(--theme-primary)" }}
              >
                {submitting ? "Cancelling..." : "Confirm cancellation"}
              </button>
              <button
                type="button"
                onClick={close}
                className="text-sm text-theme-text opacity-70 hover:opacity-100 transition-opacity"
              >
                Never mind
              </button>
            </div>
          </form>
      </div>
    </div>
  );
};

export default SubscriptionCancel;
